/**
 * Label resolution where the catalogue may not yet carry a key.
 * OWNER: web-frontend-engineer.
 *
 * The law is `t()`: a missing key throws, because a silent fallback is how an
 * unwritten string reaches a client. That law holds for every key this app
 * NAMES. This module exists for the narrow case where the key is derived from
 * DATA the catalogue has not caught up with — `rules.json` ships fifteen weave
 * values and eight pattern values, and `design/copy*.json` carries twelve and
 * two of them.
 *
 * The choice, deliberately:
 *   · never author the missing word here (D-026(b));
 *   · never render the raw id to a client — `linen_plain` is not English;
 *   · state the facts we DO have and omit the one we do not;
 *   · make the gap visible: it is logged as a defect in development and it is
 *     enumerated by `tests/web/copy-catalogue.test.ts`, which fails the moment
 *     the gap widens.
 */

import { hasCopy, tDynamic } from '@pt/copy';

const reported = new Set<string>();

/** The catalogue's word for this key, or `null` where it has none. */
export function optionalLabel(key: string): string | null {
  if (hasCopy(key)) return tDynamic(key);
  if (!reported.has(key) && typeof console !== 'undefined') {
    reported.add(key);
    console.warn(`[copy] no catalogue key "${key}" — the fact is omitted rather than invented.`);
  }
  return null;
}

export function weaveLabel(weave: string): string | null {
  return optionalLabel(`cloth.weave.${weave}`);
}

export function patternLabel(pattern: string): string | null {
  return optionalLabel(`cloth.pattern.${pattern}`);
}

/**
 * A label with NO interpolation slot, or `null`. The cut sheet reuses labels
 * written for other screens; one that carries a slot is not a label, and using
 * it would put `{authorName}` on a document a suit is cut from.
 */
export function slotFreeLabel(key: string): string | null {
  if (!hasCopy(key)) return optionalLabel(key);
  const resolved = tDynamic(key, EMPTY_SLOTS);
  return resolved.includes('{') ? null : resolved;
}

const EMPTY_SLOTS = new Proxy(
  {},
  {
    // Any slot resolves to a marker, so a template that HAS slots is detectable
    // without throwing.
    get: () => '{',
    has: () => true,
  },
) as Record<string, string>;

/** Keep only the clauses a fact line actually has. */
export function present(parts: readonly (string | null)[]): readonly string[] {
  return parts.filter((part): part is string => part !== null && part !== '');
}
